/**
 * Settings Layout Component
 *
 * Sub-layout for settings pages with horizontal tab navigation.
 * Tabs are filtered based on user permissions.
 *
 * @module components/layout/SettingsLayout
 */

import { Navigate, NavLink, Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks';
import { Spinner } from '@/components/ui';
import { Building2, Users, CreditCard } from 'lucide-react';

// =============================================================================
// Navigation Items
// =============================================================================

interface SettingsNavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  /** Permission key required to see this tab */
  permissionKey: 'canUpdateOrgSettings' | 'canManageTeam' | 'canAccessBilling';
}

const settingsNavItems: SettingsNavItem[] = [
  {
    label: 'Organization',
    to: '/settings/organization',
    icon: <Building2 className="h-4 w-4" />,
    permissionKey: 'canUpdateOrgSettings',
  },
  {
    label: 'Team',
    to: '/settings/team',
    icon: <Users className="h-4 w-4" />,
    permissionKey: 'canManageTeam',
  },
  {
    label: 'Billing',
    to: '/settings/billing',
    icon: <CreditCard className="h-4 w-4" />,
    permissionKey: 'canAccessBilling',
  },
];

// =============================================================================
// Component
// =============================================================================

export function SettingsLayout() {
  const permissions = usePermissions();

  // Show loading state while permissions are being determined
  if (permissions.loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect to profile if user has no settings access
  if (!permissions.hasAnySettingsAccess) {
    return <Navigate to="/profile" replace />;
  }

  // Filter nav items based on permissions
  const visibleNavItems = settingsNavItems.filter(
    (item) => permissions[item.permissionKey]
  );

  // Build dynamic description based on accessible settings
  const accessibleAreas: string[] = [];
  if (permissions.canUpdateOrgSettings) accessibleAreas.push('organization');
  if (permissions.canManageTeam) accessibleAreas.push('team');
  if (permissions.canAccessBilling) accessibleAreas.push('billing');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-semibold text-gray-900">
          Settings
        </h1>
      </div>

      {/* Horizontal Tab Navigation */}
      <nav className="border-b border-gray-200">
        <div className="flex -mb-px overflow-x-auto gap-x-10">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                  isActive
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Page Content */}
      <div className="w-full">
        <Outlet />
      </div>
    </div>
  );
}
